import React, { useState } from 'react';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { ORG_DB_NAMES, ORG_LABELS } from '../../config';
import { useFetch, usePaged } from '../../hooks';
import { Alert, Badge, Button, Card, Field, Input, SearchBox, Select } from '../../ui/basics';
import { DataTable } from '../../ui/DataTable';
import NameSelect from '../../components/NameSelect';
import { Icon } from '../../ui/icons';
import { Drawer, useToast } from '../../ui/overlay';
import { rules, useFormValidation } from '../../validation';
import BulkUpload from './BulkUpload';
import { AddDocRow } from './DocEditor';
import { MAX_HOURS, UNITS, beforeError, describeHours, docName, spanError, splitHours, toHours } from './docs';

/** Which documents a reference covers: the whole client, one facility, or one job title at a facility. */
export const refLevel = (r) => (r.job_title ? 'Job title' : r.facilities ? 'Facility' : 'Client');

const orgKeyOf = (dbName) => Object.keys(ORG_DB_NAMES).find((k) => ORG_DB_NAMES[k] === dbName) || '';

const SCHEMA = {
  client_name: rules.required('Client'),
  facilities: rules.maxLength(250, 'Facility'),
  job_title: [rules.maxLength(250, 'Job title'), rules.check((v, all) => !String(v || '').trim() || String(all.facilities || '').trim() !== '', 'Add a facility too, or leave the job title empty.')],
  required_documents: rules.check((list) => list.length > 0 && list.every((d) => Number.isInteger(d.Time) && d.Time >= 1 && d.Time <= MAX_HOURS
    && (d.Remind_before === undefined || (Number.isInteger(d.Remind_before) && d.Remind_before >= 1 && d.Remind_before < d.Time))),
    'Add at least one document, and give each a valid due time (and a "remind before due" time shorter than it).'),
};

const plural = (n, word) => n + ' ' + word + (n === 1 ? '' : 's');

/** A numbered block of the form, so the steps read top to bottom. */
function Section({ step, title, help, children }) {
  return (
    <section className="stack" style={{ gap: 12, padding: 16, border: '1px solid var(--border)', borderRadius: 10 }}>
      <div className="row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}>
        <span style={{ flex: '0 0 24px', height: 24, borderRadius: '50%', background: 'var(--accent-soft)', color: 'var(--accent)', fontWeight: 700, fontSize: 13, display: 'grid', placeItems: 'center' }}>{step}</span>
        <div><h3 style={{ margin: 0, fontSize: 15 }}>{title}</h3>{help && <p className="muted" style={{ margin: '2px 0 0', fontSize: 13 }}>{help}</p>}</div>
      </div>
      {children}
    </section>
  );
}

/** A plain-words statement of which candidates this reference will serve, updated as the fields change. */
function ScopeSummary({ client, facility, job }) {
  const [c, f, j] = [client, facility, job].map((x) => String(x || '').trim());
  let text = 'Pick a client to see which candidates this will apply to.';
  if (c && j && f) text = 'Used only for ' + j + ' candidates of ' + c + ' at ' + f + '.';
  else if (c && f) text = 'Used for every candidate of ' + c + ' at ' + f + ', for any job title, unless a job-title reference exists.';
  else if (c) text = 'Used for every candidate of ' + c + ', at any facility and any job title, unless a more specific reference exists.';
  return <Alert tone="info"><b>Applies to:</b> {text}</Alert>;
}

/** Amount + unit inputs for a time span in hours. Keeps its own text while typing; reports the span (or 0 when unusable). */
function SpanInput({ hours, onChange, label, disabled }) {
  const [text, setText] = useState(() => splitHours(hours));
  const problem = spanError(text.amount, text.unit);
  const update = (next) => { setText(next); onChange(spanError(next.amount, next.unit) ? 0 : toHours(next.amount, next.unit)); };
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="row" style={{ gap: 6 }}>
        <Input type="number" min="1" style={{ width: 80 }} invalid={Boolean(problem)} disabled={disabled} value={text.amount}
          onChange={(e) => update({ ...text, amount: e.target.value })} aria-label={label + ' amount'} />
        <Select style={{ width: 110 }} disabled={disabled} value={text.unit} onChange={(e) => update({ ...text, unit: e.target.value })} options={UNITS} aria-label={label + ' unit'} />
      </div>
      {problem && <span className="err" role="alert">{problem}</span>}
    </div>
  );
}

/** Optional "heads-up before it is due" time. Blank means none. Reports the hours, undefined when blank, or 0 while unusable. */
function BeforeInput({ doc, label, disabled, onChange }) {
  const [text, setText] = useState(() => (doc.Remind_before ? splitHours(doc.Remind_before) : { amount: '', unit: 'days' }));
  const problem = beforeError(text.amount, text.unit, doc.Time);
  const update = (next) => {
    setText(next);
    const blank = String(next.amount).trim() === '';
    onChange(blank ? undefined : spanError(next.amount, next.unit) ? 0 : toHours(next.amount, next.unit));
  };
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="row" style={{ gap: 6 }}>
        <Input type="number" min="1" style={{ width: 80 }} placeholder="None" invalid={Boolean(problem)} disabled={disabled} value={text.amount}
          onChange={(e) => update({ ...text, amount: e.target.value })} aria-label={label + ' amount'} />
        <Select style={{ width: 110 }} disabled={disabled} value={text.unit} onChange={(e) => update({ ...text, unit: e.target.value })} options={UNITS} aria-label={label + ' unit'} />
        <span className="muted">before it is due</span>
      </div>
      {problem && <span className="err" role="alert">{problem}</span>}
    </div>
  );
}

/** One required document. Closed it is a single summary line; open it shows the due time and the reminders. */
function DocCard({ doc, recruiters, editable, startOpen, onChange, onRemove }) {
  const [open, setOpen] = useState(Boolean(startOpen));
  const [who, setWho] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState('days');
  const [tried, setTried] = useState(false);
  const name = docName(doc);
  const reminders = Object.entries(doc.Reminder_details || {});
  const label = (key) => (recruiters.find((r) => (r.email || '').toLowerCase() === key.toLowerCase()) || { name: key }).name;
  const has = (r) => reminders.some(([key]) => key === r.name || key.toLowerCase() === (r.email || '').toLowerCase());
  const whoError = !who ? 'Pick a recruiter.' : has(recruiters.find((r) => r.name === who) || {}) ? 'This recruiter already has a reminder for this document.' : '';
  const amountError = spanError(amount, unit);
  const setReminders = (next) => onChange({ ...doc, Reminder_details: next });
  const add = () => {
    setTried(true);
    if (whoError || amountError) return;
    setReminders({ ...doc.Reminder_details, [who]: toHours(amount, unit) });
    setWho(''); setAmount(''); setTried(false);
  };
  const bad = !(Number.isInteger(doc.Time) && doc.Time >= 1);
  return (
    <div style={{ border: '1px solid ' + (bad ? 'var(--danger, #d92d20)' : 'var(--border)'), borderRadius: 8, background: 'var(--surface)' }}>
      <div className="row" style={{ padding: '10px 12px', flexWrap: 'nowrap' }}>
        <button type="button" className="link" style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, textAlign: 'left', color: 'inherit', textDecoration: 'none' }}
          aria-expanded={open} aria-label={(open ? 'Hide details of ' : 'Show details of ') + name} onClick={() => setOpen(!open)}>
          <span style={{ display: 'inline-flex', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}><Icon name="chevron" size={16} /></span>
          <b>{name}</b>
        </button>
        <Badge>{bad ? 'Set a due time' : 'Due within ' + describeHours(doc.Time)}</Badge>
        {doc.Remind_before ? <Badge tone="accent">Heads-up {describeHours(doc.Remind_before)} before</Badge> : null}
        <Badge tone={reminders.length ? 'accent' : ''}>{reminders.length ? plural(reminders.length, 'reminder') : 'No reminders'}</Badge>
        {editable && <Button variant="ghost" size="sm" aria-label={'Remove ' + name} onClick={onRemove}>Remove</Button>}
      </div>
      {open && (
        <div className="stack" style={{ padding: '4px 12px 14px 36px', gap: 14 }}>
          <Field label="Candidate must provide it within"><SpanInput hours={doc.Time} label={name + ' due time'} disabled={!editable} onChange={(Time) => onChange({ ...doc, Time })} /></Field>
          <Field label="Remind the onboarding executive before it is due" hint="Optional. One email to the person assigned to the offer (or the person who added it) if the document is still missing. Leave empty for none.">
            <BeforeInput doc={doc} label={name + ' remind before due'} disabled={!editable}
              onChange={(b) => { const next = { ...doc }; if (b === undefined) delete next.Remind_before; else next.Remind_before = b; onChange(next); }} />
          </Field>
          <div className="stack" style={{ gap: 8 }}>
            <div><b style={{ fontSize: 13 }}>Reminder emails to recruiters</b>
              <p className="muted" style={{ margin: '2px 0 0', fontSize: 13 }}>If the document is still missing, the recruiter gets an email. The time counts from the day the candidate is added.</p></div>
            {reminders.length === 0 && <span className="muted">No reminders yet. Nobody is emailed if this document is late.</span>}
            {reminders.map(([recipient, hours]) => (
              <div key={recipient} className="row" style={{ flexWrap: 'nowrap' }}>
                <span className="grow"><b>{label(recipient)}</b> <span className="muted">is emailed after {describeHours(hours)}</span></span>
                {editable && <Button variant="ghost" size="sm" aria-label={'Remove reminder for ' + label(recipient)} onClick={() => { const next = { ...doc.Reminder_details }; delete next[recipient]; setReminders(next); }}>Remove</Button>}
              </div>
            ))}
            {editable && (
              <div className="row" style={{ alignItems: 'flex-start' }}>
                <Field label="Email this recruiter" className="grow" error={tried ? whoError : ''}>
                  <Select value={who} invalid={Boolean(tried && whoError)} onChange={(e) => setWho(e.target.value)} placeholder="Choose a recruiter" options={recruiters.map((r) => ({ value: r.name, label: r.name }))} aria-label={'Recruiter to remind about ' + name} />
                </Field>
                <Field label="After" error={tried ? amountError : ''}>
                  <Input type="number" min="1" style={{ width: 80 }} invalid={Boolean(tried && amountError)} value={amount} onChange={(e) => setAmount(e.target.value)} aria-label="Amount" />
                </Field>
                <Field label=" "><Select style={{ width: 110 }} value={unit} onChange={(e) => setUnit(e.target.value)} options={UNITS} aria-label="Unit" /></Field>
                <Field label=" "><Button size="sm" icon="plus" onClick={add}>Add reminder</Button></Field>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Add a client reference (no `reference`) or edit one. The same two steps either way:
 * 1) which candidates it applies to, 2) the documents they must provide, with due times and reminders.
 */
function ReferenceEditor({ reference, org, onClose, onSaved }) {
  const { user, orgs } = useAuth();
  const toast = useToast();
  const editing = Boolean(reference);
  const editable = Boolean(user.can_edit);
  const writable = orgs.filter((o) => o !== 'combined');
  const [orgKey, setOrgKey] = useState(editing ? orgKeyOf(reference.organization) : org === 'combined' ? writable[0] : org);
  const original = editing ? { client_name: reference.client_name || '', facilities: reference.facilities || '', job_title: reference.job_title || '' } : null;
  const [form, setForm] = useState(original || { client_name: '', facilities: '', job_title: '' });
  const [docs, setDocs] = useState(editing ? reference.required_documents || [] : []);
  const [fresh, setFresh] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const lookups = useFetch('lookups/', { organization: orgKey });
  const recruiters = lookups.data ? lookups.data.recruiters : [];
  const v = useFormValidation({ ...form, required_documents: docs }, SCHEMA, original);
  const bind = (key) => ({ value: form[key], disabled: !editable, onChange: (e) => setForm({ ...form, [key]: e.target.value }), ...v.props(key) });
  // Each organization has its own clients, so the client picked for the old one must not carry over.
  const changeOrg = (key) => { setOrgKey(key); setForm((prev) => ({ ...prev, client_name: '' })); };

  const save = async () => {
    setError('');
    if (!v.check()) return;
    setBusy(true);
    try {
      if (editing) await api.post('Update_reference_details/', { reference_id: reference.reference_id, ...form, required_document: docs });
      else await api.post('Add_Reference_details/', { ...form, required_documents: docs, organization: ORG_DB_NAMES[orgKey] });
      toast(editing ? 'Reference updated' : 'Reference added', 'success');
      onSaved();
    } catch (e) { setError(v.fail(e) ? 'The server found a problem with the highlighted fields.' : e.message); setBusy(false); }
  };
  const title = !editing ? 'Add client reference' : editable ? 'Edit reference · ' + reference.client_name : [reference.client_name, reference.facilities, reference.job_title].filter(Boolean).join(' · ');
  return (
    <Drawer title={title} wide onClose={onClose}
      footer={editable ? <><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={save}>{editing ? 'Save changes' : 'Add reference'}</Button></> : <Button onClick={onClose}>Close</Button>}>
      <div className="stack">
        {(v.summary || error) && <Alert>{error || v.summary}</Alert>}
        {editing && editable && <Alert tone="info">Changes apply to candidates added from now on. Candidates already onboarded keep the documents and reminders they were given.</Alert>}

        <Section step="1" title="Who this applies to" help="Choose a client. Add a facility and a job title only if these documents are specific to them.">
          <div className="form-grid">
            <Field label="Organization" className="full" hint={!editing && org === 'combined' ? 'Choose which organization this reference belongs to.' : undefined}>
              {!editing && org === 'combined'
                ? <Select value={orgKey} onChange={(e) => changeOrg(e.target.value)} options={writable.map((o) => ({ value: o, label: ORG_LABELS[o] }))} />
                : <Input value={ORG_LABELS[orgKey] || ''} readOnly />}
            </Field>
            <Field label="Client *" error={v.shown('client_name')} hint="Pick from this organization's clients. Add a new client in the Directory first.">
              <NameSelect kind="client" lookups={lookups} current={original ? original.client_name : ''} {...bind('client_name')} />
            </Field>
            <Field label="Facility" error={v.shown('facilities')} hint="Optional. Leave empty to cover every facility of the client.">
              <Input maxLength={250} placeholder="Any facility" {...bind('facilities')} /></Field>
            <Field label="Job title" className="full" error={v.shown('job_title')} hint={form.facilities.trim() ? 'Optional. Leave empty to cover every job title at this facility.' : 'Add a facility first to make this reference specific to a job title.'}>
              <Input maxLength={250} placeholder="Any job title" {...bind('job_title')} disabled={!editable || !form.facilities.trim()} /></Field>
          </div>
          <ScopeSummary client={form.client_name} facility={form.facilities} job={form.job_title} />
        </Section>

        <Section step="2" title={'Documents candidates must provide (' + docs.length + ')'} help="Give each document a due time. Optionally email a recruiter if it is still missing. Click a document to see or change its details.">
          {v.shown('required_documents') && <span className="err" role="alert">{v.shown('required_documents')}</span>}
          {docs.length === 0 && <p className="muted" style={{ margin: 0 }}>No documents yet. Add the first one below.</p>}
          {docs.map((doc, i) => (
            <DocCard key={docName(doc)} doc={doc} recruiters={recruiters} editable={editable} startOpen={docName(doc) === fresh}
              onChange={(next) => setDocs(docs.map((d, j) => (j === i ? next : d)))} onRemove={() => setDocs(docs.filter((_, j) => j !== i))} />
          ))}
          {editable && (
            <AddDocRow onAdd={(doc) => { setDocs([...docs, doc]); setFresh(docName(doc)); }} existing={docs.map(docName)} label="Add document" />
          )}
        </Section>
      </div>
    </Drawer>
  );
}

export default function References({ org }) {
  const { user } = useAuth();
  const paged = usePaged('references/', { fixed: { organization: org }, initialOrdering: 'client_name' });
  const { filters, setFilter, reload } = paged;
  const [dialog, setDialog] = useState(null);
  const columns = [
    { key: 'client_name', label: 'Client', sortable: true, render: (r) => <b>{r.client_name}</b> },
    { key: 'facilities', label: 'Facility', sortable: true, render: (r) => r.facilities || <span className="muted">Any facility</span> },
    { key: 'job_title', label: 'Job title', sortable: true, render: (r) => r.job_title || <span className="muted">Any job title</span> },
    { key: 'level', label: 'Applies to', render: (r) => <Badge tone={r.job_title ? 'accent' : ''}>{refLevel(r) === 'Client' ? 'Whole client' : refLevel(r) === 'Facility' ? 'Whole facility' : 'Job title'}</Badge> },
    { key: 'docs', label: 'Required documents', render: (r) => <Badge>{(r.required_documents || []).length} documents</Badge> },
  ];
  return (
    <>
      <Card flush>
        <div className="toolbar">
          <SearchBox value={filters.search} onChange={(v) => setFilter('search', v)} placeholder="Search client, facility or job title…" />
          <div className="right row">{user.can_edit && <Button icon="upload" onClick={() => setDialog({ type: 'bulk' })}>Bulk upload</Button>}{user.can_edit && <Button variant="primary" icon="plus" onClick={() => setDialog({ type: 'add' })}>Add reference</Button>}</div>
        </div>
        <DataTable columns={columns} paged={paged} rowKey={(r) => r.reference_id} noun="references" onRowClick={(row) => setDialog({ type: 'view', row })} />
      </Card>
      {dialog && dialog.type === 'bulk' && <BulkUpload org={org} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); reload(); }} />}
      {dialog && dialog.type === 'add' && <ReferenceEditor org={org} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); reload(); }} />}
      {dialog && dialog.type === 'view' && <ReferenceEditor reference={dialog.row} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); reload(); }} />}
    </>
  );
}
